/**
 * Servizio per l'integrazione con GrowthBook
 */

import { GrowthBook } from '@growthbook/growthbook';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';
import { getGrowthBookConfig, getEnvironment, isDevelopment, getConfig } from '$lib/config/environment.js';

let growthbookInstance = null;
let initializationPromise = null;

/**
 * Ottiene il cookie gbuuid esistente se presente
 * @returns {string|null} L'UUID del cookie gbuuid o null se non esiste
 */
function getExistingGbUuid() {
  if (typeof document === 'undefined') return null;
  
  const cookies = document.cookie.split('; ');
  const gbuuidCookie = cookies.find(row => row.startsWith('gbuuid='));
  return gbuuidCookie ? gbuuidCookie.split('=')[1] : null;
}

/**
 * Assicura che GrowthBook sia inizializzato prima di restituire l'istanza
 * @returns {Promise<GrowthBook|null>} L'istanza di GrowthBook inizializzata
 */
export async function ensureGrowthBookInitialized() {
  if (growthbookInstance) {
    return growthbookInstance;
  }
  
  if (initializationPromise) {
    return await initializationPromise;
  }
  
  return await initializeGrowthBook();
}

/**
 * Inizializza GrowthBook con configurazione di base
 * @returns {Promise<GrowthBook>} L'istanza di GrowthBook inizializzata
 */
export async function initializeGrowthBook() {
  if (typeof window === 'undefined') {
    console.warn('GrowthBook non può essere inizializzato in ambiente server-side');
    return null;
  }

  if (growthbookInstance) {
    console.debug('GrowthBook già inizializzato');
    return growthbookInstance;
  }

  // Se c'è già un'inizializzazione in corso, aspetta quella
  if (initializationPromise) {
    return await initializationPromise;
  }

  // Crea la promise di inizializzazione
  initializationPromise = (async () => {
    try {
      // Ottieni configurazione per l'ambiente corrente
      const config = getGrowthBookConfig();
      const environment = getEnvironment();
      
      // Controlla se esiste già un UUID nel cookie gbuuid
      const existingUuid = getExistingGbUuid();
      
      // Genera o usa UUID esistente per l'attributo 'id'
      let userId = existingUuid;
      if (!userId) {
        // Genera nuovo UUID se non esiste
        userId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
        // Salva nel cookie
        document.cookie = `gbuuid=${userId}; path=/; max-age=${60 * 60 * 24 * 365}; secure; samesite=lax`;
      }
      
      console.debug(`Inizializzando GrowthBook per ambiente: ${environment}`, { 
        userId,
        config: { ...config, clientKey: config.clientKey ? config.clientKey.substring(0, 10) + '...' : 'missing' }
      });

      // Configurazione dell'auto-attributes plugin (per altri attributi)
      const autoAttributesConfig = {
        uuidCookieName: 'gbuuid',
        uuidAutoPersist: false, // Gestiamo manualmente
        uuid: userId
      };

      // Custom fetch wrapper per gestire errori HTML/JSON
      const safeFetch = async (url, options = {}) => {
        try {
          console.debug('[GrowthBook] Fetching:', url);
          const response = await fetch(url, options);

          if (!response.ok) {
            console.error('[GrowthBook] HTTP error:', response.status, response.statusText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.error('[GrowthBook] Wrong content-type:', contentType);
            throw new Error(`Expected JSON but got: ${contentType}`);
          }

          return response;
        } catch (error) {
          console.error('[GrowthBook] Fetch error:', error);
          throw error;
        }
      };

      // Crea l'istanza GrowthBook con configurazione ambiente-specifica
      growthbookInstance = new GrowthBook({
        apiHost: config.apiHost,
        clientKey: config.clientKey,
        enableDevMode: config.enableDevMode,
        fetcher: safeFetch, // Use custom fetch wrapper
        plugins: [
          autoAttributesPlugin(autoAttributesConfig)
        ],
        // Attributi ambiente-specifici per targeting
        attributes: {
          // Identificatori
          id: userId, // IMPORTANTE: attributo 'id' per gli esperimenti (hash attribute)
          deviceId: userId, // Stesso dell'id per consistenza
          
          // URL e navigazione
          url: window.location.href,
          path: window.location.pathname,
          host: window.location.hostname,
          query: window.location.search,
          
          // Device detection
          deviceType: /mobile|android|iphone|ipad|tablet/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          
          // Browser detection
          browser: (() => {
            const ua = navigator.userAgent.toLowerCase();
            if (ua.includes('chrome') && !ua.includes('edge')) return 'chrome';
            if (ua.includes('edge')) return 'edge';
            if (ua.includes('firefox')) return 'firefox';
            if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
            return 'unknown';
          })(),
          
          // UTM parameters
          utmSource: new URLSearchParams(window.location.search).get('utm_source') || undefined,
          utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
          utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
          utmTerm: new URLSearchParams(window.location.search).get('utm_term') || undefined,
          utmContent: new URLSearchParams(window.location.search).get('utm_content') || undefined,
          
          // Ambiente e metadata
          environment: environment,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        // Callback per tracciare gli esperimenti
        trackingCallback: (experiment, result) => {
          // Usa 'this' per accedere all'istanza GrowthBook invece di growthbookInstance
          const userId = growthbookInstance?.getAttributes()?.id || 'anonymous';

          if (typeof window.gtag === 'function') {
            window.gtag('set', 'user_properties', {
              user_id: userId, 
            });
          }

          if (isDevelopment()) {
            console.debug('[GrowthBook] Esperimento visto:', {
              environment,
              experimentId: experiment.key,
              variationId: result.variationId,
              variationKey: result.key,
              variationName: result.name,
              featureId: result.featureId,
              inExperiment: result.inExperiment,
              userId
            });
          }

          // Integrazione con Google Analytics 4 (GA4 → BigQuery → GrowthBook)
          if (typeof window !== 'undefined') {
            // Funzione per inviare l'evento a GA4 tramite GTM dataLayer
            const sendToGA4 = () => {
              // GTM usa dataLayer, non gtag direttamente
              const hasDataLayer = window.dataLayer && Array.isArray(window.dataLayer);
              const hasGtag = typeof window.gtag === 'function';

              if (!hasDataLayer && !hasGtag) {
                console.warn('[GrowthBook] Né dataLayer né gtag disponibili, riprovo...');
                return false;
              }

              const eventData = {
                // Parametri standard GrowthBook per GA4
                experiment_id: experiment.key,
                variation_id: String(result.variationId), // Deve essere stringa per GA4

                // Parametri aggiuntivi utili per analisi
                experiment_name: experiment.name || experiment.key,
                variation_name: result.name || result.key,

                // Metadati contesto
                user_id: userId,
                in_experiment: result.inExperiment ? 'true' : 'false', // Stringa per GA4
                environment: environment,

                // Debug info (opzionale)
                hash_attribute: result.hashAttribute,
                feature_id: result.featureId || undefined
              };

              // Prova prima con gtag (se disponibile)
              if (hasGtag) {
                window.gtag('event', 'experiment_viewed', eventData);

                if (isDevelopment()) {
                  console.debug('[GrowthBook] Evento inviato a GA4 tramite gtag:', {
                    event: 'experiment_viewed',
                    ...eventData
                  });
                } else {
                  console.log('[GrowthBook] Esperimento tracciato (gtag):', experiment.key, '→', result.key);
                }
              }
              // Altrimenti usa dataLayer (GTM)
              else if (hasDataLayer) {
                // Invia evento a GTM con formato compatibile per le variabili GTM
                window.dataLayer.push({
                  event: 'experiment_viewed',
                  // Formato per variabili GTM esistenti (se configurate)
                  experimentId: experiment.key,
                  variationId: String(result.variationId),
                  // Formato con underscore per GA4 diretto
                  experiment_id: experiment.key,
                  variation_id: String(result.variationId),
                  // Altri parametri
                  experiment_name: experiment.name || experiment.key,
                  variation_name: result.name || result.key,
                  user_id: userId,
                  in_experiment: result.inExperiment ? 'true' : 'false',
                  environment: environment,
                  hash_attribute: result.hashAttribute,
                  feature_id: result.featureId || undefined,

                  // Invia l'ID come parametro custom che GTM mapperà
                  // al Data Layer Variable e poi alla User Property (se non usi gtag)
                  gb_anon_id: userId, // Usiamo un nome esplicito per GTM
                });

                if (isDevelopment()) {
                  console.debug('[GrowthBook] Evento inviato a GA4 tramite dataLayer:', {
                    event: 'experiment_viewed',
                    experimentId: experiment.key,
                    variationId: result.variationId
                  });
                } else {
                  console.log('[GrowthBook] Esperimento tracciato (dataLayer):', experiment.key, '→', result.key);
                }
              }

              return true;
            };

            // Prova subito a inviare
            if (!sendToGA4()) {
              // Se né gtag né dataLayer sono disponibili, attendi e riprova
              let attempts = 0;
              const maxAttempts = 20; // 20 tentativi = 10 secondi
              const interval = setInterval(() => {
                attempts++;
                if (sendToGA4() || attempts >= maxAttempts) {
                  clearInterval(interval);
                  if (attempts >= maxAttempts) {
                    console.error('[GrowthBook] Tracking non disponibile dopo 10 secondi, evento perso:', experiment.key);
                    console.error('Debug: dataLayer disponibile?', typeof window.dataLayer, 'gtag disponibile?', typeof window.gtag);
                  }
                }
              }, 500); // Riprova ogni 500ms
            }
          }
        }
      });

      // Inizializza con timeout ambiente-specifico
      let result;
      try {
        result = await growthbookInstance.init({
          timeout: config.timeout,
          streaming: config.streaming
        }).catch(error => {
          // Catch promise rejection from init
          console.error(`[GrowthBook] Init promise rejected for ${environment}:`, error);
          return { success: false, error: error.message || String(error) };
        });

        if (result && result.success) {
          console.debug(`GrowthBook inizializzato con successo per ${environment}`, {
            source: result.source,
            attributes: growthbookInstance.getAttributes()
          });
        } else {
          console.warn(`GrowthBook inizializzato ma con avvertimenti per ${environment}`, {
            source: result?.source,
            error: result?.error
          });
        }
      } catch (initError) {
        console.error(`Errore durante init di GrowthBook per ${environment}:`, initError);
        console.warn('Continuando con GrowthBook in modalità fallback senza feature flags remoti');
        // Non lanciare l'errore, continua con l'istanza anche se init fallisce
      }

      window._growthbook = growthbookInstance;
      window._growthbook_env = environment;

      // Gestione queue globale
      window.growthbook_queue = window.growthbook_queue || [];
      while (window.growthbook_queue.length > 0) {
        const fn = window.growthbook_queue.shift();
        if (typeof fn === 'function') {
          try {
            fn(growthbookInstance);
          } catch (error) {
            console.error('Errore eseguendo funzione dalla growthbook_queue:', error);
          }
        }
      }

      window.growthbook_queue.push = function(fn) {
        if (typeof fn === 'function') {
          try {
            fn(growthbookInstance);
          } catch (error) {
            console.error('Errore eseguendo funzione dalla growthbook_queue:', error);
          }
        }
      };

      return growthbookInstance;

    } catch (error) {
      console.error(`Errore durante l'inizializzazione di GrowthBook per ${getEnvironment()}:`, error);
      growthbookInstance = null;
      return null;
    } finally {
      initializationPromise = null;
    }
  })();

  return await initializationPromise;
}

/**
 * Ottiene l'istanza GrowthBook corrente
 * @returns {GrowthBook|null} L'istanza di GrowthBook o null se non inizializzata
 */
export function getGrowthBook() {
  return growthbookInstance;
}

/**
 * Aggiorna gli attributi utente in GrowthBook
 * @param {Object} newAttributes - Nuovi attributi da impostare
 */
export function updateUserAttributes(newAttributes) {
  if (!growthbookInstance) {
    console.warn('GrowthBook non inizializzato, impossibile aggiornare gli attributi');
    return;
  }

  const currentAttributes = growthbookInstance.getAttributes();
  growthbookInstance.setAttributes({
    ...currentAttributes,
    ...newAttributes
  });

  console.debug('Attributi GrowthBook aggiornati:', growthbookInstance.getAttributes());
}

/**
 * Imposta l'ID utente in GrowthBook mantenendo l'UUID esistente se presente
 * @param {string} userId - L'ID dell'utente autenticato
 * @param {string} [existingAbUuid] - UUID esistente dall'utente (se presente)
 */
export function setAuthenticatedUser(userId, existingAbUuid = null) {
  if (!growthbookInstance) {
    console.warn('GrowthBook non inizializzato, impossibile impostare l\'utente');
    return;
  }

  const currentAttributes = growthbookInstance.getAttributes();
  
  // Se l'utente ha un abUuid salvato nel profilo, usa quello
  // Altrimenti mantieni l'UUID già generato dal plugin auto-attributes
  const finalId = existingAbUuid || currentAttributes.id || userId;

  console.debug('Impostando utente autenticato in GrowthBook:', {
    userId,
    existingAbUuid,
    currentId: currentAttributes.id,
    finalId
  });

  updateUserAttributes({
    id: finalId,
    userId: userId, // Mantieni l'ID utente originale come attributo separato
    authenticated: true
  });

  // Se l'UUID finale è diverso da quello nel cookie, aggiorna il cookie
  const cookieUuid = getExistingGbUuid();
  if (cookieUuid !== finalId) {
    document.cookie = `gbuuid=${finalId}; path=/; max-age=${60 * 60 * 24 * 365}; secure; samesite=lax`;
    console.debug('Cookie gbuuid aggiornato:', finalId);
  }

  return finalId;
}


/**
 * Ottiene il valore di una feature flag
 * @param {string} featureKey - Chiave della feature flag
 * @param {any} defaultValue - Valore di default
 * @returns {Promise<any>} Il valore della feature flag
 */
export async function getFeatureValue(featureKey, defaultValue = null) {
  const gb = await ensureGrowthBookInitialized();
  if (!gb) {
    console.warn('GrowthBook non disponibile, restituendo valore di default per:', featureKey);
    return defaultValue;
  }

  return gb.getFeatureValue(featureKey, defaultValue);
}

/**
 * Versione sincrona per ottenere il valore di una feature flag (usa l'istanza esistente)
 * @param {string} featureKey - Chiave della feature flag
 * @param {any} defaultValue - Valore di default
 * @returns {any} Il valore della feature flag
 */
export function getFeatureValueSync(featureKey, defaultValue = null) {
  if (!growthbookInstance) {
    console.warn('GrowthBook non inizializzato, restituendo valore di default per:', featureKey);
    return defaultValue;
  }

  return growthbookInstance.getFeatureValue(featureKey, defaultValue);
}

/**
 * Verifica se una feature flag è attiva
 * @param {string} featureKey - Chiave della feature flag
 * @returns {Promise<boolean>} True se la feature è attiva
 */
export async function isFeatureOn(featureKey) {
  const gb = await ensureGrowthBookInitialized();
  if (!gb) {
    console.warn('GrowthBook non disponibile, restituendo false per:', featureKey);
    return false;
  }

  return gb.isOn(featureKey);
}

/**
 * Versione sincrona per verificare se una feature flag è attiva
 * @param {string} featureKey - Chiave della feature flag
 * @returns {boolean} True se la feature è attiva
 */
export function isFeatureOnSync(featureKey) {
  if (!growthbookInstance) {
    console.warn('GrowthBook non inizializzato, restituendo false per:', featureKey);
    return false;
  }

  return growthbookInstance.isOn(featureKey);
}

/**
 * Ottiene informazioni dettagliate su una feature
 * @param {string} featureKey - Chiave della feature flag
 * @returns {Object} Informazioni dettagliate sulla feature
 */
export function evaluateFeature(featureKey) {
  if (!growthbookInstance) {
    console.warn('GrowthBook non inizializzato per la valutazione di:', featureKey);
    return { value: null, source: 'error' };
  }

  return growthbookInstance.evalFeature(featureKey);
}

/**
 * Aggiorna le feature flags (utile per il refresh manuale)
 */
export async function refreshFeatures() {
  if (!growthbookInstance) {
    console.warn('GrowthBook non inizializzato, impossibile aggiornare le feature');
    return;
  }

  try {
    await growthbookInstance.refreshFeatures();
    console.debug('Feature flags aggiornate');
  } catch (error) {
    console.error('Errore durante l\'aggiornamento delle feature flags:', error);
  }
}

/**
 * Restituisce tutte le feature definite lato GrowthBook (mappa delle definizioni)
 * Nota: i valori devono essere valutati per utente tramite getFeatureValue.
 * @returns {Object} Mappa delle feature oppure oggetto vuoto
 */
export function getAllFeaturesSync() {
  if (!growthbookInstance) return {};
  try {
    return growthbookInstance.getFeatures?.() || {};
  } catch (_) {
    return {};
  }
}
